import { useState, useEffect, useRef } from 'react'
import { sessions } from '../api/client'
import type { SubmitSessionParams } from '../api/client'
import { useSessionMode } from '../professional/SessionModeContext'

export interface GameCompletionParams {
  gameComplete:  boolean
  activitySlug:  string
  difficulty:    'easy' | 'medium' | 'hard'
  moves:         number
  startTime:     number | null
  endTime:       number | null
  pairsTotal:    number
  pairsMatched:  number
  /** Pass only if the activity supports multiple sub-modes */
  mode?:         string
  /** Pass only if the game supports theme selection */
  themeId?:      number
  /** If provided, uses the start/complete flow; otherwise falls back to legacy submit */
  sessionId?:    string | null
  /** Called after the standard 600 ms delay to transition to the complete screen */
  onCompleted:   () => void
}

/**
 * Handles game-session submission and screen-transition on completion.
 *
 * - In professional session mode: delegates to `sessionMode.onComplete()`.
 * - When `sessionId` is provided: calls `POST /sessions/complete`.
 * - Otherwise: submits via the legacy `POST /sessions` endpoint and exposes
 *   `saveError` when the request fails so the UI can inform the user.
 */
export function useGameCompletion({
  gameComplete,
  activitySlug,
  difficulty,
  moves,
  startTime,
  endTime,
  pairsTotal,
  pairsMatched,
  mode,
  themeId,
  sessionId,
  onCompleted,
}: GameCompletionParams): { saveError: boolean; clearSaveError: () => void } {
  const sessionMode       = useSessionMode()
  const [saveError, setSaveError] = useState(false)

  // Keep a stable ref so the effect never needs `onCompleted` as a dependency.
  const onCompletedRef = useRef(onCompleted)
  useEffect(() => { onCompletedRef.current = onCompleted })

  // Prevent re-triggering when dependencies (e.g. mode, difficulty) change while
  // gameComplete is still true — e.g. user changes mode on the setup screen after
  // a game ends, before the next resetGame() call clears gameComplete.
  const completionFiredRef = useRef(false)

  useEffect(() => {
    if (!gameComplete) {
      completionFiredRef.current = false
      return
    }
    if (completionFiredRef.current) return
    completionFiredRef.current = true

    const durationMs = endTime && startTime ? endTime - startTime : undefined

    if (sessionMode) {
      sessionMode.onComplete({ durationMs, moves, pairsTotal, pairsMatched })
    } else if (sessionId) {
      sessions.complete({ sessionId, durationMs, moves, pairsTotal, pairsMatched, mode, themeId })
        .catch((err) => {
          console.error('[useGameCompletion] Failed to complete session:', err)
          setSaveError(true)
        })
    } else {
      const params: SubmitSessionParams = {
        activitySlug,
        status:     'COMPLETED',
        difficulty,
        durationMs,
        moves,
        pairsTotal,
        pairsMatched,
      }
      if (mode    !== undefined) params.mode    = mode
      if (themeId !== undefined) params.themeId = themeId

      sessions.submit(params).catch((err) => {
        console.error('[useGameCompletion] Failed to submit session:', err)
        setSaveError(true)
      })
    }

    const timer = setTimeout(() => onCompletedRef.current(), 600)
    return () => clearTimeout(timer)
  }, [gameComplete, activitySlug, difficulty, mode, moves, startTime, endTime, pairsTotal, pairsMatched, themeId, sessionId, sessionMode])

  // saveError is only meaningful while the game is complete; derive false when not
  const clearSaveError = () => {
    setSaveError(false)
    try { sessionStorage.removeItem('save-error-dismissed') } catch { /* ignore */ }
  }
  return { saveError: gameComplete && saveError, clearSaveError }
}
